import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Switch, FormControlLabel, Grid, Button,
  Snackbar, Alert, CircularProgress, Divider, Chip
} from '@mui/material';
import {
  Email, Notifications, Phone, Save
} from '@mui/icons-material';

const API = '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const NOTIF_TYPES = [
  { type: 'INSCRIPTION', label: 'Inscriptions', icon: '📝', color: '#f59e0b' },
  { type: 'NOTE_PUBLIEE', label: 'Notes publiées', icon: '📊', color: '#3b82f6' },
  { type: 'PAIEMENT_RECU', label: 'Paiements', icon: '💰', color: '#10b981' },
  { type: 'PRESENCE', label: 'Présences', icon: '✅', color: '#10b981' },
  { type: 'ABSCENCE_JUSTIFIEE', label: 'Absences justifiées', icon: '✅', color: '#10b981' },
  { type: 'RAPPEL_COURS', label: 'Rappels de cours', icon: '⏰', color: '#ef4444' },
  { type: 'COMMUNICATION', label: 'Communications', icon: '📢', color: '#8b5cf6' },
  { type: 'PASSATION', label: 'Passations', icon: '🔄', color: '#8b5cf6' },
  { type: 'SYSTEME', label: 'Système', icon: '🔔', color: '#6b7280' },
  { type: 'MESSAGE', label: 'Messages', icon: '💬', color: '#8b5cf6' }
];

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notification-preferences`, { headers: authHeaders() });
      const existing = res.data.data || [];
      const merged = NOTIF_TYPES.map(nt => {
        const found = existing.find(p => p.type === nt.type);
        return {
          type: nt.type,
          channels: found ? found.channels : { email: true, push: true, sms: false }
        };
      });
      setPrefs(merged);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur chargement préférences:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const toggleChannel = (type, channel) => {
    setPrefs(prev => prev.map(p =>
      p.type === type ? { ...p, channels: { ...p.channels, [channel]: !p.channels[channel] } } : p
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-preferences`, { preferences: prefs }, { headers: authHeaders() });
      setSnackbar({ type: 'success', text: 'Préférences enregistrées' });
    } catch (err) {
      setSnackbar({ type: 'error', text: 'Erreur d\'enregistrement' });
    }
    setSaving(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>🔔 Préférences de notification</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choisissez les notifications que vous souhaitez recevoir et par quel canal.
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>Type d'événement</Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', minWidth: 240, justifyContent: 'flex-end' }}>
              <Chip icon={<Notifications />} label="Push" size="small" variant="outlined" sx={{ minWidth: 70 }} />
              <Chip icon={<Email />} label="Email" size="small" variant="outlined" sx={{ minWidth: 70 }} />
              <Chip icon={<Phone />} label="SMS" size="small" variant="outlined" sx={{ minWidth: 70 }} />
            </Box>
          </Box>

          {prefs.map((p, i) => {
            const nt = NOTIF_TYPES.find(t => t.type === p.type);
            return (
              <Box key={p.type} sx={{
                px: 3, py: 1.5,
                display: 'flex', alignItems: 'center',
                bgcolor: i % 2 === 0 ? 'white' : '#fafafa',
                borderBottom: i < prefs.length - 1 ? '1px solid #f3f4f6' : 'none'
              }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{nt?.icon || '🔔'}</Typography>
                  <Typography variant="body2">{nt?.label || p.type}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 240, justifyContent: 'flex-end' }}>
                  <Switch
                    checked={p.channels.push}
                    onChange={() => toggleChannel(p.type, 'push')}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: p.channels.push ? '#8b5cf6' : undefined } }}
                  />
                  <Switch
                    checked={p.channels.email}
                    onChange={() => toggleChannel(p.type, 'email')}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: p.channels.email ? '#3b82f6' : undefined } }}
                  />
                  <Switch
                    checked={p.channels.sms}
                    onChange={() => toggleChannel(p.type, 'sms')}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: p.channels.sms ? '#10b981' : undefined } }}
                  />
                </Box>
              </Box>
            );
          })}

          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb' }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {snackbar ? <Alert severity={snackbar.type} onClose={() => setSnackbar(null)}>{snackbar.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default NotificationPreferences;


